/**
 * FolderPlanPreview · 文件夹方案预览组件 (v6 重构 · live/history 统一入口)
 *
 * 单一路径:
 *   · 接受 card: FolderPlanCard + messageId
 *   · 数据归一在 usePlanPreview 内
 *   · UI 渲染交给 FolderPlanPreviewView (纯展示)
 *
 * v6 重构前曾有 LiveVariant (selection 单例 + anchor) 和 HistoryVariant (message
 * 私有) 两条分支; 重构后合并 — 每条 folder_plan card 都自带快照数据, live 和
 * history 共用同一渲染路径。
 *
 * 调用点: MessageAssistant 的 CardSwitch 看到 card.kind='folder_plan' 时挂
 *   <FolderPlanPreview card={card} messageId={message.id} />
 */

import React from 'react'
import { useAppSelector } from '../../../../app/hooks'
import { selectIsLastMessage } from '../../../../slices/aiClassify'
import type { FolderPlanCard } from '../../../../types/ai-classify'
import FolderPlanPreviewView from './FolderPlanPreviewView'
import FollowUpChips from './FollowUpChips'
import { usePlanPreview } from './usePlanPreview'

export interface FolderPlanPreviewProps {
  card: FolderPlanCard
  /** assistant 消息 id · confirm 后写回 card.taskCreated 用 */
  messageId: string
}

const FolderPlanPreview: React.FC<FolderPlanPreviewProps> = ({
  card,
  messageId
}) => {
  const { viewProps } = usePlanPreview(card, messageId)
  const isLastMessage = useAppSelector(selectIsLastMessage(messageId))
  /**
   * 「你可以继续问」追问 chips · 仅当本卡是会话**最后一条消息**且可编辑时展示：
   *  · readonly（已确认 / 被新方案取代 / 老数据）不展示
   *  · 之后出现任何后续消息（点击 chip 发出的 user 消息、助手回复等）
   *    isLastMessage 翻 false，chips 立即随组件卸载消失
   *  · folders hydrate 完成后再挂载：避免 SSE 流中对仍在变更的维度链发 preview
   */
  const showFollowUps =
    isLastMessage &&
    viewProps.mode === 'history_editable' &&
    card.folders !== undefined &&
    !!card.sessionId
  return (
    <>
      <FolderPlanPreviewView {...viewProps} />
      {showFollowUps && <FollowUpChips sessionId={String(card.sessionId)} />}
    </>
  )
}

export default FolderPlanPreview
